import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { ChevronDown, ExternalLink, Paperclip, Send, X } from 'lucide-react'
import Select from '@/components/ui/Select'
import { uploadFile, type FileRef } from '@/api/files'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import { useTemplates } from '@/features/messaging/useMessaging'
import { MESSAGE_TOKENS, insertTokenAt } from '@/features/messaging/tokens'
import RichTextEditor, { type RichTextEditorHandle } from '@/components/RichTextEditor'
import RichText from '@/components/RichText'
import InsertTokenMenu from '@/components/ui/InsertTokenMenu'
import { cn } from '@/lib/cn'
import { useAuthStore } from '@/features/auth/authStore'
import type { Contact } from '@/api/types'
import { sendTestToMe, type PreviewTarget } from '@/api/messages'
import { bodyForRichEditor, htmlToPlainText, plainTextToHtml } from '@/lib/messageFormat'
import { useMessagePreview } from '@/features/messaging/useMessagePreview'
import MessagePreviewPane from '@/features/messaging/MessagePreviewPane'
import SaveAsTemplateModal from '@/features/messaging/SaveAsTemplateModal'
import MySignatureModal from '@/features/team/MySignatureModal'

/** The recipient fields the preview understands — any contact-shaped object supplies them. */
type PreviewContact = Pick<Contact, 'firstName' | 'lastName' | 'email'>

/** Rough SMS length — one GSM segment per 160 characters (concatenation ignored). */
const SMS_SEGMENT = 160
/** Auto-grow ceiling (matches max-h-60 = 15rem). */
const MAX_TEXTAREA_PX = 240

/** The backend swaps an empty personalization value for "there"; the preview mirrors that so it
 *  never shows a raw {{token}} or a stray "Hi ,". */
const PREVIEW_FALLBACK = 'there'

/** SMS opt-out line the backend appends to every outbound SMS (AU SPAM Act); mirrored in the preview. */
const SMS_OPT_OUT = 'Reply STOP to opt out.'

/** Every token the preview resolves: the shared message set plus {{email}} (used in templates). */
const PREVIEW_TOKENS = MESSAGE_TOKENS

/** The sending agent, for resolving {{agent_*}} in the preview (approximated as the current user). */
interface PreviewAgent {
  fullName?: string
  email?: string
}

/** Value for a token in the preview: the contact supplies name/email, the current user approximates
 *  the sending agent ({{agent_*}}), and anything else the composer can't know falls back like the backend. */
function previewValue(
  token: string,
  c: PreviewContact | undefined,
  agent: PreviewAgent,
  extra: Record<string, string>,
): string {
  // Caller-supplied real values (e.g. {{deal_title}} from the open deal) win — without them a
  // context token the composer can't know would wrongly fall back to "there".
  const name = token.slice(2, -2).trim()
  if (extra[name]?.trim()) {
    return extra[name].trim()
  }
  switch (token) {
    case '{{first_name}}':
      return c?.firstName?.trim() || PREVIEW_FALLBACK
    case '{{last_name}}':
      return c?.lastName?.trim() || PREVIEW_FALLBACK
    case '{{email}}':
      return c?.email?.trim() || PREVIEW_FALLBACK
    case '{{agent_name}}':
      return agent.fullName?.trim() || PREVIEW_FALLBACK
    case '{{agent_email}}':
      return agent.email?.trim() || PREVIEW_FALLBACK
    case '{{agent_signature}}':
      return '' // the signature is applied server-side (email only); never literal text in the preview
    default:
      return PREVIEW_FALLBACK
  }
}

/** Substitutes every known token (with fallbacks) so the preview matches what the backend sends. */
function fillPlaceholders(
  text: string,
  c: PreviewContact | undefined,
  agent: PreviewAgent,
  extra: Record<string, string> = {},
): string {
  let out = text
  for (const token of PREVIEW_TOKENS) {
    const name = token.slice(2, -2).trim()
    out = out.replace(new RegExp(`\\{\\{\\s*${name}\\s*\\}\\}`, 'gi'), previewValue(token, c, agent, extra))
  }
  return out
}

/**
 * Controlled message editor shared by the Contacts campaign bar and the Lists send panel.
 * A trigger button expands a panel with the channel/template pickers, a full-size auto-growing
 * body textarea (with a character / SMS-segment counter and a live preview), and the Send button.
 * Picking a template LOADS its subject/body into the editable draft rather than locking the composer,
 * so the agent always sees — and can edit — the exact copy that will go out; tokens are still
 * rendered per recipient server-side on send.
 */
export default function MessageComposer({
  channel,
  onChannelChange,
  templateId,
  onTemplateChange,
  subject = '',
  onSubjectChange,
  body,
  onBodyChange,
  recipientCount,
  onSend,
  sending = false,
  sampleContact,
  open,
  onOpenChange,
  tone = 'default',
  tokens,
  sendLabel,
  allowAttachments = false,
  attachments = [],
  onAttachmentsChange,
  inline = false,
  previewValues = {},
  previewTarget,
  customFields,
}: {
  channel: string
  onChannelChange: (channel: string) => void
  templateId: string
  onTemplateChange: (templateId: string) => void
  /** Email subject (email channel only; ignored for SMS and when a template is picked). */
  subject?: string
  onSubjectChange?: (subject: string) => void
  body: string
  onBodyChange: (body: string) => void
  recipientCount: number
  /** Send it; `includeSignature` is the composer's "Add signature" switch (email only). */
  onSend: (opts: { includeSignature: boolean }) => void
  sending?: boolean
  /** First recipient, if known — powers the placeholder preview. */
  sampleContact?: PreviewContact
  /** Controlled expand state; omit to let the composer manage its own. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** "onDark" styles the trigger for the dark campaign bar; "default" for a light card. */
  tone?: 'default' | 'onDark'
  /** Insertable token chips shown under the body (omit to hide). Ignored when a template is picked. */
  tokens?: string[]
  /** Overrides the default "Send to {count}" button label (e.g. a single-recipient deal send). */
  sendLabel?: string
  /** Show the email attachment picker (single-recipient sends). Attachments apply to email only. */
  allowAttachments?: boolean
  /** Controlled attachment list; the parent owns it and sends the keys. */
  attachments?: FileRef[]
  onAttachmentsChange?: (files: FileRef[]) => void
  /** Always-open, no "Compose" trigger — for panels that already have their own "Send message" heading. */
  inline?: boolean
  /** Real values for tokens the composer can't derive (e.g. {{deal_title}}), keyed by token name
   *  without braces. Used only for the live preview; the backend fills them for real on send. */
  previewValues?: Record<string, string>
  /** Render the preview on the server (the real sender, signature and footer) for this deal/contact.
   *  Without it the composer falls back to an approximate client-side preview. */
  previewTarget?: PreviewTarget
  /** Whose custom fields "Insert" offers (default: the contact's). A deal message passes both. */
  customFields?: ('contact' | 'deal')[]
}) {
  const { data: templates } = useTemplates(channel)
  const currentUser = useAuthStore((s) => s.user)
  const [internalOpen, setInternalOpen] = useState(false)
  const isOpen = inline || (open ?? internalOpen)
  const taRef = useRef<HTMLTextAreaElement>(null)
  const editorRef = useRef<RichTextEditorHandle>(null)
  const subjectRef = useRef<HTMLInputElement>(null)
  // Which field a token chip inserts into — the last of subject/body the user focused.
  const activeField = useRef<'subject' | 'body'>('body')
  const [uploadingFile, setUploadingFile] = useState(false)
  const [attachError, setAttachError] = useState<string | null>(null)
  const [includeSignature, setIncludeSignature] = useState(true)
  const [editingSignature, setEditingSignature] = useState(false)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [savedTemplate, setSavedTemplate] = useState<string | null>(null)
  const isAdmin = currentUser?.role === 'AGENCY_ADMIN' || currentUser?.role === 'PLATFORM_ADMIN'

  async function onPickAttachment(e: ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? [])
    e.target.value = '' // allow re-selecting the same file after a remove
    if (!picked.length || !onAttachmentsChange) return
    setUploadingFile(true)
    setAttachError(null)
    // One at a time so a file that fails (too large…) doesn't drop the ones that uploaded.
    let next = attachments
    for (const f of picked) {
      try {
        next = [...next, await uploadFile(f)]
        onAttachmentsChange(next)
      } catch (err) {
        // Surface the real reason (storage not configured / too large / auth) rather than always blaming size.
        setAttachError(`${f.name}: ${apiErrorMessage(err)}`)
      }
    }
    setUploadingFile(false)
  }

  function toggle() {
    const next = !isOpen
    if (onOpenChange) onOpenChange(next)
    else setInternalOpen(next)
  }

  // Channel and template are linked: a channel's templates don't apply to another channel.
  function changeChannel(next: string) {
    // email bodies are HTML, SMS is plain text — convert the draft rather than show raw tags
    if (next !== channel && body.trim()) {
      onBodyChange(
        next === 'sms'
          ? // SMS carries no signature (it renders empty there), so drop the token rather than leave a blank line
            htmlToPlainText(body).replace(/\n?\{\{\s*agent_signature\s*\}\}/g, '').trim()
          : plainTextToHtml(body),
      )
    }
    onChannelChange(next)
    onTemplateChange('')
  }

  /**
   * Copy a template's subject/body into the editable draft and clear the template selection, so what
   * the agent sees is exactly what will be sent — and they can change it first. (Previously picking a
   * template blanked and disabled the body, which read as "templates don't work".)
   */
  function loadTemplate(id: string) {
    const t = (templates ?? []).find((x) => x.id === id)
    if (!t) return
    if (channel === 'email' && onSubjectChange && t.subject) onSubjectChange(t.subject)
    onBodyChange(channel === 'email' ? bodyForRichEditor(t.body ?? '') : (t.body ?? ''))
    onTemplateChange('')
  }

  const usingTemplate = !!templateId
  // Email bodies use the rich editor; SMS (and the disabled template state) keep the plain textarea.
  const richBody = channel === 'email' && !usingTemplate

  // Insert a token at the caret: into the rich editor as literal {{token}} text for email, or into
  // the textarea (keeping focus just past it) for SMS.
  function insertToken(token: string) {
    // Subject supports the same tokens (the backend renders them) — insert there when it was
    // the last focused field, so personalising the subject is discoverable, not just typeable.
    if (activeField.current === 'subject' && channel === 'email' && !usingTemplate && onSubjectChange) {
      const el = subjectRef.current
      const { next, caret } = insertTokenAt(el, subject, token)
      onSubjectChange(next)
      requestAnimationFrame(() => {
        el?.focus()
        el?.setSelectionRange(caret, caret)
      })
      return
    }
    if (richBody) {
      editorRef.current?.insertText(token)
      return
    }
    const el = taRef.current
    const { next, caret } = insertTokenAt(el, body, token)
    onBodyChange(next)
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(caret, caret)
    })
  }

  const activeTemplate = usingTemplate ? (templates ?? []).find((t) => t.id === templateId) : undefined
  const segments = Math.max(1, Math.ceil(body.length / SMS_SEGMENT))
  // A manually composed email needs a subject — SendGrid rejects a blank one. Templates carry their own.
  const needsSubject = channel === 'email' && !usingTemplate
  const subjectMissing = needsSubject && !subject.trim()
  const sendDisabled = sending || recipientCount === 0 || (!templateId && !body.trim()) || subjectMissing
  // Say why Send is greyed out — a silent disabled button reads as "sending is broken".
  const sendBlockedReason = sending
    ? null
    : uploadingFile
      ? 'Wait for the file to finish uploading'
      : recipientCount === 0
        ? 'Choose who to send to'
        : subjectMissing
          ? 'Add a subject to send'
          : !templateId && !body.trim()
            ? 'Write a message to send'
            : null

  const [confirming, setConfirming] = useState(false)
  // "Send me a test" — campaigns only (one recipient is its own test), email, rendered for the sample contact.
  const canTest = recipientCount > 1 && channel === 'email' && previewTarget?.kind === 'contact' && !sendDisabled
  const [testState, setTestState] = useState<string | null>(null)
  async function sendTest() {
    if (previewTarget?.kind !== 'contact') return
    setTestState('sending')
    try {
      const to = await sendTestToMe(previewTarget.id, {
        channel,
        templateId: templateId || null,
        subject: templateId ? null : subject,
        body: templateId ? null : body,
        includeSignature,
      })
      setTestState(`Test sent to ${to}`)
    } catch (err) {
      setTestState(apiErrorMessage(err))
    }
    setTimeout(() => setTestState(null), 6000)
  }

  // Auto-grow the textarea to fit its content (DOM-only — no state, so no effect-setState lint).
  useEffect(() => {
    const el = taRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_PX)}px`
  }, [body, isOpen, templateId])

  // Approximate the sending agent as the current user so {{agent_*}} previews as the real name,
  // not the "there" fallback. (In bulk the true sender is each contact's owner; this is the common case.)
  const previewAgent: PreviewAgent = { fullName: currentUser?.fullName, email: currentUser?.email }

  // Mirror the backend's appended SMS opt-out line so the preview matches what actually goes out.
  const withSmsOptOut = (text: string): string =>
    channel === 'sms' && !text.toLowerCase().includes('stop') ? `${text}\n${SMS_OPT_OUT}` : text

  // Always preview once there's something to send — agents said they won't send from a CRM they can't
  // see the output of. Without a known recipient the tokens fall back to their neutral defaults.
  let preview: string | null = null
  if (usingTemplate) {
    preview = activeTemplate?.body
      ? withSmsOptOut(fillPlaceholders(activeTemplate.body, sampleContact, previewAgent, previewValues))
      : activeTemplate
        ? `Template “${activeTemplate.name}” will be used.`
        : null
  } else if (body.trim()) {
    preview = withSmsOptOut(fillPlaceholders(body, sampleContact, previewAgent, previewValues))
  }

  // The subject line as it will arrive, tokens and all — the backend personalizes it the same way.
  const previewSubject =
    channel === 'email'
      ? fillPlaceholders(
          (usingTemplate ? activeTemplate?.subject : subject) ?? '',
          sampleContact,
          previewAgent,
          previewValues,
        ).trim()
      : ''

  const serverPreview = useMessagePreview(isOpen ? previewTarget : undefined, {
    channel,
    templateId: templateId || null,
    subject: channel === 'email' ? subject : null,
    body,
    includeSignature,
  })

  // Who the server says this goes out as (the deal/contact owner) — whose signature it carries.
  const signedBy = previewTarget && channel === 'email' ? serverPreview.data?.fromName ?? null : null

  const triggerCls =
    tone === 'onDark'
      ? 'flex items-center gap-1.5 px-3 py-1.5 bg-white text-brand-darkest rounded-lg text-sm font-medium hover:bg-white/90'
      : 'flex items-center gap-1 text-xs text-brand-primary hover:text-brand-dark'

  return (
    <>
      {/* Inline mode: the parent already shows a "Send message" heading, so skip the Compose toggle. */}
      {!inline && (
        <button type="button" onClick={toggle} className={triggerCls}>
          <Send size={tone === 'onDark' ? 14 : 13} /> Compose
          <ChevronDown size={13} className={cn('transition-transform', isOpen && 'rotate-180')} />
        </button>
      )}

      {isOpen && (
        <div className={cn(
          'w-full rounded-lg border border-gray-200 bg-white p-3 space-y-2 text-left text-brand-dark',
          !inline && 'mt-2',
        )}>
          <div className="flex flex-wrap gap-2">
            <Select size="sm" value={channel} onChange={(e) => changeChannel(e.target.value)} className="w-28">
              <option value="email">email</option>
              <option value="sms">sms</option>
            </Select>
            {/* A template LOADS into the editable fields instead of locking the composer: the agent
                sees the real copy, can edit it before sending, and the preview always works.
                Tokens stay in the text and are still rendered per recipient on send. */}
            <Select size="sm" value="" onChange={(e) => loadTemplate(e.target.value)} className="flex-1 min-w-40">
              <option value="">— load a template —</option>
              {(templates ?? []).map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </Select>
            {isAdmin && (
              // Opens beside the message rather than replacing it — the draft stays where it is.
              <a
                href="/settings?tab=templates"
                target="_blank"
                rel="noopener noreferrer"
                className={cn('self-center inline-flex items-center gap-1 text-xs hover:underline', tone === 'onDark' ? 'text-white/80' : 'text-brand-primary')}
              >
                Edit templates <ExternalLink size={11} />
              </a>
            )}
          </div>

          {needsSubject && (
            <input
              ref={subjectRef}
              type="text"
              value={subject}
              onChange={(e) => onSubjectChange?.(e.target.value)}
              onFocus={() => (activeField.current = 'subject')}
              placeholder="Subject"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
            />
          )}

          <div onFocusCapture={() => (activeField.current = 'body')}>
          {richBody ? (
            <RichTextEditor
              ref={editorRef}
              value={body}
              onChange={onBodyChange}
              placeholder="Write your message…"
            />
          ) : (
            <textarea
              ref={taRef}
              value={usingTemplate ? '' : body}
              onChange={(e) => onBodyChange(e.target.value)}
              disabled={usingTemplate}
              rows={4}
              placeholder={usingTemplate ? 'Using the template' : 'Write your message…'}
              className="w-full resize-y min-h-[5.5rem] max-h-60 overflow-y-auto px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40 disabled:bg-gray-50 disabled:text-gray-400"
            />
          )}
          </div>


          <div className="flex items-center justify-between gap-2 text-[11px] text-gray-400">
            {tokens && !usingTemplate ? (
              <InsertTokenMenu tokens={tokens} onInsert={insertToken} tone={tone} customFields={customFields} />
            ) : (
              <span />
            )}
            {!usingTemplate && channel === 'sms' && (
              <span className="whitespace-nowrap">
                {body.length} chars
                {body.length > 0 && ` · ${segments} SMS`}
              </span>
            )}
          </div>

          {previewTarget && preview && (
            <MessagePreviewPane preview={serverPreview.data} loading={serverPreview.isFetching} subjectMissing={subjectMissing} />
          )}

          {!previewTarget && preview && channel === 'email' && (
            // Shown as the recipient receives it — sender, subject, signature and opt-out footer and
            // all. The client's CEO named this as the reason he never sends from a CRM: "it's my
            // professional image at stake… I'm reluctant to send that because I don't know what it's
            // going to look like."
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-200 px-2.5 py-1.5 space-y-0.5">
                <span className="text-[10px] uppercase tracking-wide text-gray-400 block">
                  What the recipient sees
                </span>
                <div className="text-[11px] text-gray-500">
                  <span className="text-gray-400">From </span>
                  {currentUser?.fullName ?? 'You'}
                  {currentUser?.email ? ` <${currentUser.email}>` : ''}
                </div>
                {sampleContact && (
                  <div className="text-[11px] text-gray-500">
                    <span className="text-gray-400">To </span>
                    {[sampleContact.firstName, sampleContact.lastName].filter(Boolean).join(' ') || 'Contact'}
                    {sampleContact.email ? ` <${sampleContact.email}>` : ''}
                  </div>
                )}
                {previewSubject && (
                  <div className="text-[11px] text-brand-dark font-medium break-words">{previewSubject}</div>
                )}
              </div>
              <div className="p-2.5 bg-white">
                <RichText html={preview} className="text-xs text-gray-600" />
                {currentUser?.emailSignature && (
                  <RichText html={currentUser.emailSignature} className="text-xs text-gray-600 mt-3" />
                )}
                <hr className="border-0 border-t border-gray-200 my-3" />
                <p className="text-[11px] text-gray-400 m-0">
                  Don’t want these emails? <span className="underline">Unsubscribe</span>.
                </p>
              </div>
            </div>
          )}

          {!previewTarget && preview && channel !== 'email' && (
            <div className="rounded-lg bg-gray-50 border border-gray-100 p-2 text-xs text-gray-600">
              <span className="text-[10px] uppercase tracking-wide text-gray-400 block mb-0.5">Preview</span>
              <span className="whitespace-pre-wrap break-words">{preview}</span>
            </div>
          )}

          {(channel === 'email' || (isAdmin && !usingTemplate && body.trim())) && (
            <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 text-xs', tone === 'onDark' ? 'text-white/80' : 'text-gray-600')}>
              {channel === 'email' && (
                <>
                  {/* Each email can go without the signature (a quick reply); on by default. */}
                  <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={includeSignature}
                      onChange={(e) => setIncludeSignature(e.target.checked)}
                      className="rounded border-gray-300 text-brand-primary focus:ring-brand-primary/40"
                    />
                    Add signature
                  </label>
                  {/* The email goes out as the record's owner — offer to edit the signature only when that's you. */}
                  {signedBy && signedBy !== currentUser?.fullName ? (
                    <span className={tone === 'onDark' ? 'text-white/60' : 'text-gray-400'}>{signedBy}’s, as the owner</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setEditingSignature(true)}
                      className={cn('hover:underline', tone === 'onDark' ? 'text-white' : 'text-brand-primary')}
                    >
                      Edit my signature
                    </button>
                  )}
                </>
              )}
              {isAdmin && !usingTemplate && body.trim() && (
                <button
                  type="button"
                  onClick={() => setSavingTemplate(true)}
                  className={cn('ml-auto hover:underline', tone === 'onDark' ? 'text-white' : 'text-brand-primary')}
                >
                  Save as template
                </button>
              )}
              {savedTemplate && <span className="w-full text-success">Saved as the template “{savedTemplate}”.</span>}
            </div>
          )}

          {allowAttachments && channel === 'email' && (
            <div className="space-y-1.5">
              {attachments.map((f) => (
                <div key={f.key} className="flex items-center gap-2 text-xs text-gray-600">
                  <Paperclip size={12} className="shrink-0 text-gray-400" />
                  <span className="truncate">{f.filename}</span>
                  <button
                    type="button"
                    onClick={() => onAttachmentsChange?.(attachments.filter((a) => a.key !== f.key))}
                    className="text-gray-300 hover:text-danger shrink-0"
                    title="Remove attachment"
                  >
                    <X size={13} />
                  </button>
                </div>
              ))}
              <label className="inline-flex items-center gap-1.5 text-xs text-brand-primary cursor-pointer hover:underline">
                <Paperclip size={12} />
                {uploadingFile ? 'Uploading…' : 'Attach a file'}
                <input type="file" multiple className="hidden" onChange={onPickAttachment} disabled={uploadingFile} />
              </label>
              {attachError && <p className="text-danger text-[11px]">{attachError}</p>}
            </div>
          )}

          {/* A campaign (more than one person) asks first, and can be tried on yourself before it goes. */}
          {confirming ? (
            <div className={cn('flex flex-wrap items-center justify-end gap-2 rounded-lg px-3 py-2', tone === 'onDark' ? 'bg-white/10' : 'bg-amber-50 border border-amber-200')}>
              <span className={cn('text-sm mr-auto', tone === 'onDark' ? 'text-white' : 'text-amber-900')}>
                Send {channel === 'email' && (activeTemplate?.subject || subject) ? `“${activeTemplate?.subject || subject}” ` : ''}to {recipientCount} people now? This can’t be undone.
              </span>
              <button type="button" onClick={() => setConfirming(false)} className={cn('px-3 py-1.5 text-sm rounded-lg', tone === 'onDark' ? 'text-white/80 hover:text-white' : 'text-gray-600 hover:text-brand-dark')}>
                Not yet
              </button>
              <button
                type="button"
                onClick={() => { setConfirming(false); onSend({ includeSignature }) }}
                disabled={sending}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-60"
              >
                <Send size={14} /> Yes, send it
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-end gap-3">
              {canTest && (
                <span className={cn('text-xs mr-auto', tone === 'onDark' ? 'text-white/70' : 'text-gray-500')}>
                  {testState === 'sending' ? 'Sending a test…' : testState ? testState : (
                    <button type="button" onClick={sendTest} className={cn('underline', tone === 'onDark' ? 'hover:text-white' : 'hover:text-brand-primary')}>
                      Send me a test
                    </button>
                  )}
                </span>
              )}
              {sendBlockedReason && (
                <span className={cn('text-xs', tone === 'onDark' ? 'text-white/70' : 'text-gray-500')}>{sendBlockedReason}</span>
              )}
              <button
                type="button"
                onClick={() => (recipientCount > 1 ? setConfirming(true) : onSend({ includeSignature }))}
                disabled={sendDisabled || uploadingFile}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-60"
              >
                <Send size={14} /> {sending ? 'Sending…' : (sendLabel ?? `Send to ${recipientCount}`)}
              </button>
            </div>
          )}
        </div>
      )}
      {editingSignature && <MySignatureModal onClose={() => setEditingSignature(false)} />}
      {savingTemplate && (
        <SaveAsTemplateModal
          channel={channel === 'sms' ? 'sms' : 'email'}
          subject={subject}
          body={body}
          onClose={() => setSavingTemplate(false)}
          onSaved={(name) => {
            setSavingTemplate(false)
            setSavedTemplate(name)
          }}
        />
      )}
    </>
  )
}
